import { getComponentRadius } from 'features/assetModeling'
import {
  GeckoComponentDataset,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  RolodexEntryPutJson,
  RolodexEntryType,
} from 'types'

import { DEFAULT_FFS_LTA_DETAIL_CARDS } from './ffsDetailCards'
import { LOCAL_THIN_AREA_INDICATION_SET_SORT_ORDER } from './ffsIds'

// API 579 Annex 4B bin-sizing constants. Duplicated locally so the libra step
// avoids importing from `pages/`.
const FORMULA_FACTOR = 0.138
export const MIN_BIN_SIZE_INCHES = 0.0625
export const MIN_BIN_SIZE_MM = 1.6
export const READING_SPACING_UPPER_BOUND_LIMIT_INCHES = 1
export const READING_SPACING_UPPER_BOUND_LIMIT_METRIC = 25

export interface BinSizeBreakdown {
  binSize: number
  correctedThickness: number
  readingSpacingUpperBound: number
}

export function toMeasurement(
  value: number | null | undefined,
  unit: GeckoMeasurementUnit
): GeckoMeasurement | null {
  return value === null || value === undefined
    ? null
    : GeckoMeasurement.CreateFromValue(value, unit)
}

export function computeBinSizeBreakdown({
  assetOuterDiameter,
  uniformThickness,
  futureCorrosionAllowance,
  lengthUnit,
}: {
  assetOuterDiameter: GeckoMeasurement | null
  uniformThickness: GeckoMeasurement | null
  futureCorrosionAllowance: GeckoMeasurement | null
  lengthUnit: GeckoMeasurementUnit
}): BinSizeBreakdown | null {
  if (
    assetOuterDiameter === null ||
    uniformThickness === null ||
    futureCorrosionAllowance === null
  ) {
    return null
  }

  const diameter = assetOuterDiameter.convertTo(lengthUnit).num
  const uniformThicknessValue = uniformThickness.convertTo(lengthUnit).num
  const futureCorrosionAllowanceValue =
    futureCorrosionAllowance.convertTo(lengthUnit).num

  if (
    uniformThicknessValue <= 0 ||
    uniformThicknessValue <= futureCorrosionAllowanceValue ||
    diameter <= 0
  ) {
    return null
  }

  const correctedThickness =
    uniformThicknessValue - futureCorrosionAllowanceValue

  const isInches = lengthUnit.id === GeckoMeasurementUnit.Inches.id
  const upperBoundLimit = isInches
    ? READING_SPACING_UPPER_BOUND_LIMIT_INCHES
    : READING_SPACING_UPPER_BOUND_LIMIT_METRIC
  const minBinSize = isInches ? MIN_BIN_SIZE_INCHES : MIN_BIN_SIZE_MM

  const readingSpacingUpperBound = Math.min(
    2 * uniformThicknessValue,
    upperBoundLimit
  )
  const formulaResult =
    FORMULA_FACTOR * Math.sqrt(diameter * correctedThickness)
  const binSize = Math.max(
    Math.min(formulaResult, 0.5 * readingSpacingUpperBound),
    minBinSize
  )

  return { binSize, correctedThickness, readingSpacingUpperBound }
}

/**
 * Derives the asset outer diameter (D) from a component model.
 * Ports `getAssetOuterDiameter` from
 * `pages/details/extensions/fde/ffsLtaGeneration/calculations.ts:34-47`.
 */
export function getAssetOuterDiameter(
  componentModel: Parameters<typeof getComponentRadius>[0],
  lengthUnit: GeckoMeasurementUnit
): GeckoMeasurement | null {
  const radius = getComponentRadius(componentModel)
  if (!radius) {
    return null
  }
  return GeckoMeasurement.CreateFromValue(
    radius.convertTo(lengthUnit).num * 2,
    lengthUnit
  )
}

/**
 * Computes the future-corrosion allowance from a corrosion rate and a time
 * window: FCA = CR * time. Ports the FCA-calculator helper from
 * `FfsLtaGenerationSidebar.tsx:206-211`.
 */
export function computeCalculatedFutureCorrosionAllowance({
  corrosionRate,
  years,
}: {
  corrosionRate: GeckoMeasurement | null
  years: GeckoMeasurement | null
}): GeckoMeasurement | null {
  if (!corrosionRate || !years) {
    return null
  }
  const rateUnit = corrosionRate.unit
  // Rate is typically mm/year or in/year; convert years to years and multiply.
  const yearsValue = years.convertTo(GeckoMeasurementUnit.Years).num
  if (!Number.isFinite(yearsValue) || yearsValue <= 0) {
    return null
  }
  // The corrosion rate's "numerator" unit is the thickness unit per year. We
  // grab the rate's numeric value as-is and treat the resulting product as a
  // length measurement in the same thickness unit.
  const rateValue = corrosionRate.num
  if (!Number.isFinite(rateValue) || rateValue <= 0) {
    return null
  }
  // Map the per-year rate unit to its thickness equivalent. The asset model
  // uses standard `<unit>PerYear` enum values, so peel off the suffix.
  const thicknessUnitId = rateUnit.id.replace(/PerYear$/i, '')
  const thicknessUnit =
    GeckoMeasurementUnit.GetByID(thicknessUnitId) ??
    GeckoMeasurementUnit.Millimeters
  return GeckoMeasurement.CreateFromValue(rateValue * yearsValue, thicknessUnit)
}

/**
 * Builds the Rolodex PUT payload for the LTA indication set produced by the
 * "Generate Local Thin Areas" action. Ports `buildLocalThinAreaIndicationSetEntry`
 * from `pages/details/extensions/fde/ffsLtaGeneration/entryBuilders.ts:18-52`.
 *
 * Pure shape-builder: callers compute the `id` via `resolveFfsArtifactIds`
 * from `./ffsIds` and pass it in, so the asset-vs-legacy
 * branching lives at the call site (`FfsBinningStep`) alongside the matching
 * read-path branching (`useActiveLtaSet`). Persists the canonical FFS LTA
 * detail-card list (`DEFAULT_FFS_LTA_DETAIL_CARDS` in `./ffsDetailCards`) so the
 * triage step's indication detail panel has all of its expected affordances.
 */
export function buildLocalThinAreaIndicationSetEntry(
  cds: GeckoComponentDataset,
  id: string
): RolodexEntryPutJson {
  return {
    id,
    type: RolodexEntryType.INDICATION_SET,
    data: {
      title: 'LTAs',
      detailCards: [...DEFAULT_FFS_LTA_DETAIL_CARDS],
      classificationConfig: [],
      allowCreateOrChangeRegion: false,
      sortOrder: LOCAL_THIN_AREA_INDICATION_SET_SORT_ORDER,
    },
    tags: cds.tags,
  }
}
